import { isUsEquityRegularSessionOpen, remainingDte } from "../shared/marketHours";
import { computeRhOptionPnl } from "../shared/rhOptionQuote";
import { fetchAgenticEquityQuotes, fetchAgenticEquitySectors } from "../api/agenticClient";
import {
  loadOwnLedgerOpenOptions,
  type OwnLedgerOpenOption,
} from "./ownLedgerClient";
import { loadOwnLedgerFeatures } from "./ownLedgerFeatures";
import { tryRobinhoodPositionsSnapshot, type PositionsFeedSource as RhFeedSource } from "./rhOptionsFeed";
import type { OptionPosition, PositionsSnapshot } from "./watchlistData";

export type PositionsFeedSource = RhFeedSource | "own_ledger";

const EMPTY_POSITIONS: PositionsSnapshot = { options: [], stock: [], fetchedAt: 0 };

export function currentSessionFeedSource(): PositionsFeedSource {
  return isUsEquityRegularSessionOpen() ? "stockforge" : "robinhood";
}

export function sessionFeedSourceMismatch(
  cachedSource: PositionsFeedSource | null | undefined,
): boolean {
  if (!cachedSource) return false;
  // Own-ledger universe is always primary; don't bounce on RTH/AH feed flips.
  if (cachedSource === "own_ledger") return false;
  return cachedSource !== currentSessionFeedSource();
}

function normOptionSymbol(symbol: string | undefined | null): string {
  return String(symbol ?? "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
}

function contractKey(ticker: string, side: string, strike: number, expDate: string): string {
  return `${ticker.trim().toUpperCase()}|${String(side).trim().toLowerCase()}|${Number(strike)}|${String(expDate).slice(0, 10)}`;
}

function indexOptions(options: OptionPosition[]): {
  bySymbol: Map<string, OptionPosition>;
  byKey: Map<string, OptionPosition>;
} {
  const bySymbol = new Map<string, OptionPosition>();
  const byKey = new Map<string, OptionPosition>();
  for (const pos of options) {
    const sym = normOptionSymbol(pos.option_symbol);
    if (sym) bySymbol.set(sym, pos);
    byKey.set(contractKey(pos.ticker, pos.side, pos.strike, pos.exp_date), pos);
  }
  return { bySymbol, byKey };
}

function ledgerToBasePosition(row: OwnLedgerOpenOption): OptionPosition {
  const dte = remainingDte(row.expDate);
  return {
    option_symbol: row.optionSymbol,
    ticker: row.ticker,
    option_id: row.optionId ?? undefined,
    chain_id: row.chainId ?? undefined,
    side: row.side,
    strike: row.strike,
    exp_date: row.expDate,
    open_date: row.openDate ?? undefined,
    contracts: row.contracts,
    entry_price: row.entryPrice,
    open_credit: row.openCredit,
    dte: dte ?? undefined,
    expired: dte != null ? dte < 0 : false,
    account_id: row.accountId || undefined,
    account_count: row.accountCount && row.accountCount > 0 ? row.accountCount : undefined,
    broker_id: row.brokerId || undefined,
    broker_name: row.brokerName || undefined,
    account_label: row.accountLabel || undefined,
    account_short_name: row.accountShortName || undefined,
  };
}

function mergeLedgerWithFeeds(
  ledger: OwnLedgerOpenOption[],
  rh: PositionsSnapshot | null,
  sf: PositionsSnapshot,
  equityQuotes: Map<string, number>,
  sectors: Map<string, string>,
): OptionPosition[] {
  const rhIdx = indexOptions(rh?.options ?? []);
  const sfIdx = indexOptions(sf.options ?? []);

  const merged = ledger.map((row) => {
    const base = ledgerToBasePosition(row);
    const key = contractKey(row.ticker, row.side, row.strike, row.expDate);
    const rhPos = rhIdx.bySymbol.get(row.optionSymbol) ?? rhIdx.byKey.get(key);
    const sfPos = sfIdx.bySymbol.get(row.optionSymbol) ?? sfIdx.byKey.get(key);
    const contracts = base.contracts;
    const entryPrice = base.entry_price;
    const openCredit =
      entryPrice != null && Number.isFinite(entryPrice) ? entryPrice * 100 * contracts : base.open_credit;

    const mid =
      (rhPos?.current_price != null && Number.isFinite(rhPos.current_price)
        ? Number(rhPos.current_price)
        : null) ??
      (sfPos?.current_price != null && Number.isFinite(sfPos.current_price)
        ? Number(sfPos.current_price)
        : null);

    const stockPrice =
      (rhPos?.stock_price != null && Number.isFinite(rhPos.stock_price)
        ? Number(rhPos.stock_price)
        : null) ??
      equityQuotes.get(row.ticker) ??
      (sfPos?.stock_price != null && Number.isFinite(sfPos.stock_price)
        ? Number(sfPos.stock_price)
        : null) ??
      undefined;

    const computed = computeRhOptionPnl(row.entryPrice, mid, contracts, true);
    const profit =
      computed.profit ??
      (rhPos?.current_profit != null && Number.isFinite(rhPos.current_profit)
        ? Number(rhPos.current_profit)
        : null) ??
      (sfPos?.current_profit != null && Number.isFinite(sfPos.current_profit)
        ? Number(sfPos.current_profit)
        : null);
    const profitPct =
      computed.profitPct ??
      (rhPos?.current_profit_pct != null && Number.isFinite(rhPos.current_profit_pct)
        ? Number(rhPos.current_profit_pct)
        : null) ??
      (sfPos?.current_profit_pct != null && Number.isFinite(sfPos.current_profit_pct)
        ? Number(sfPos.current_profit_pct)
        : null);

    const sector =
      sectors.get(row.ticker)?.trim() ||
      sfPos?.sector?.trim() ||
      rhPos?.sector?.trim() ||
      undefined;

    return {
      ...base,
      option_id: row.optionId ?? rhPos?.option_id,
      chain_id: row.chainId ?? rhPos?.chain_id ?? sfPos?.chain_id,
      contracts,
      open_credit: openCredit,
      sector,
      target_price: sfPos?.target_price ?? rhPos?.target_price,
      current_price: mid ?? undefined,
      stock_price: stockPrice,
      current_profit: profit ?? undefined,
      current_profit_pct: profitPct ?? undefined,
      pop: rhPos?.pop ?? sfPos?.pop,
      collateral: sfPos?.collateral ?? rhPos?.collateral,
    };
  });
  return merged;
}

export interface OptionsPositionsResolution {
  positions: PositionsSnapshot;
  source: PositionsFeedSource;
  /** Same snapshot when quotes arrived in time; a richer book if the first paint beat Robinhood. */
  continued: Promise<{ positions: PositionsSnapshot; source: PositionsFeedSource }>;
}

/** Don't leave Options on the loading shell if Robinhood quotes stall. */
const ENRICH_DEADLINE_MS = 4_000;

interface FeedEnrichment {
  rh: PositionsSnapshot | null;
  equityQuotes: Map<string, number>;
  sectors: Map<string, string>;
}

function firstSettled<T>(promise: Promise<T>, ms: number): Promise<{ ok: true; value: T } | { ok: false }> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve({ ok: false }), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve({ ok: true, value });
      },
      () => {
        clearTimeout(timer);
        resolve({ ok: false });
      },
    );
  });
}

function finishResolution(
  result: { positions: PositionsSnapshot; source: PositionsFeedSource },
  continued?: Promise<{ positions: PositionsSnapshot; source: PositionsFeedSource }>,
): OptionsPositionsResolution {
  return { ...result, continued: continued ?? Promise.resolve(result) };
}

async function loadFeedEnrichment(
  sfSnapshot: PositionsSnapshot,
  ledger: OwnLedgerOpenOption[],
): Promise<FeedEnrichment> {
  const rh = await tryRobinhoodPositionsSnapshot(sfSnapshot, remainingDte);
  const tickers = [
    ...new Set([
      ...ledger.map((row) => row.ticker),
      ...(rh?.options ?? []).map((row) => row.ticker),
    ]),
  ];
  const [equityQuotes, sectors] = await Promise.all([
    fetchAgenticEquityQuotes(tickers).catch(() => new Map<string, number>()),
    fetchAgenticEquitySectors(tickers).catch(() => new Map<string, string>()),
  ]);
  return { rh, equityQuotes, sectors };
}

function positionsFromLedger(
  ledger: OwnLedgerOpenOption[],
  sfSnapshot: PositionsSnapshot,
  extra: FeedEnrichment | null,
): { positions: PositionsSnapshot; source: PositionsFeedSource } {
  return {
    positions: {
      options: mergeLedgerWithFeeds(
        ledger,
        extra?.rh ?? null,
        sfSnapshot,
        extra?.equityQuotes ?? new Map(),
        extra?.sectors ?? new Map(),
      ),
      stock: sfSnapshot.stock ?? [],
      fetchedAt: Date.now(),
    },
    source: "own_ledger",
  };
}

/**
 * Options universe = own ledger when enabled. RH/SF only enrich marks,
 * target, and sector — they never add or drop rows. Falls back to
 * session-gated SF/RH only when own ledger is disabled.
 *
 * Robinhood quote calls are capped so a stalled feed still returns the ledger book.
 * `continued` resolves again when those quotes arrive after the cap.
 */
export async function resolveOptionsPositionsSnapshot(
  loadSfSnapshot: (force: boolean) => Promise<PositionsSnapshot>,
  force: boolean,
): Promise<OptionsPositionsResolution> {
  const features = await loadOwnLedgerFeatures(force);
  const sfSnapshotPromise = loadSfSnapshot(force).catch(() => EMPTY_POSITIONS);

  // Own ledger is the Options universe. Do not fall back to StockForge's
  // partial /positions book when the ledger is enabled but still empty or slow.
  if (features.enabled) {
    const [ledger, sfSnapshot] = await Promise.all([
      loadOwnLedgerOpenOptions(force),
      sfSnapshotPromise,
    ]);
    const enrichPromise = loadFeedEnrichment(sfSnapshot, ledger);
    const quick = await firstSettled(enrichPromise, ENRICH_DEADLINE_MS);
    if (quick.ok) return finishResolution(positionsFromLedger(ledger, sfSnapshot, quick.value));
    return finishResolution(
      positionsFromLedger(ledger, sfSnapshot, null),
      enrichPromise.then((extra) => positionsFromLedger(ledger, sfSnapshot, extra)),
    );
  }

  const sfSnapshot = await sfSnapshotPromise;
  if (isUsEquityRegularSessionOpen()) {
    return finishResolution({ positions: sfSnapshot, source: "stockforge" });
  }
  const rhPromise = tryRobinhoodPositionsSnapshot(sfSnapshot, remainingDte);
  const quick = await firstSettled(rhPromise, ENRICH_DEADLINE_MS);
  const fromRh = (
    rh: PositionsSnapshot | null,
  ): { positions: PositionsSnapshot; source: PositionsFeedSource } =>
    rh
      ? { positions: rh, source: "robinhood" }
      : { positions: sfSnapshot, source: "stockforge" };
  if (quick.ok) return finishResolution(fromRh(quick.value));
  return finishResolution(
    fromRh(null),
    rhPromise.then((rh) => fromRh(rh)),
  );
}
